import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { pb } from '../lib/pocketbase.js';
import PartnerFormModal from '../components/PartnerFormModal.jsx';
import {
  PARTNER_TYP_BADGE,
  PARTNER_TYP_LABEL,
  PARTNER_TYP_OPTIONS,
} from '../lib/partner.js';

const TYP_FILTER_OPTIONS = [{ value: 'alle', label: 'Alle' }, ...PARTNER_TYP_OPTIONS];

export default function PartnerPage() {
  const [partner, setPartner] = useState([]);
  const [praxenCounts, setPraxenCounts] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [typFilter, setTypFilter] = useState('alle');
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [partnerList, praxen] = await Promise.all([
        pb.collection('partner').getFullList({ sort: 'name' }),
        pb.collection('praxen').getFullList({
          fields: 'partner',
          filter: 'partner != ""',
        }),
      ]);
      setPartner(partnerList);
      const counts = new Map();
      for (const p of praxen) {
        if (!p.partner) continue;
        counts.set(p.partner, (counts.get(p.partner) ?? 0) + 1);
      }
      setPraxenCounts(counts);
    } catch (err) {
      setError(err?.message || 'Partner konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return partner.filter((p) => {
      if (typFilter !== 'alle' && p.typ !== typFilter) return false;
      if (!q) return true;
      return (
        p.name?.toLowerCase().includes(q) ||
        p.kontaktperson?.toLowerCase().includes(q) ||
        p.email?.toLowerCase().includes(q)
      );
    });
  }, [partner, search, typFilter]);

  function openCreate() {
    setEditTarget(null);
    setModalOpen(true);
  }

  function openEdit(p) {
    setEditTarget(p);
    setModalOpen(true);
  }

  async function handleDelete(p) {
    const count = praxenCounts.get(p.id) ?? 0;
    const warnung =
      count > 0
        ? `\n\n⚠️ ${count} Praxis/Praxen sind diesem Partner als Empfehler zugeordnet — der Bezug wird beim Löschen entfernt.`
        : '';
    if (!window.confirm(`Partner "${p.name}" wirklich löschen?${warnung}`)) {
      return;
    }
    try {
      await pb.collection('partner').delete(p.id);
      setPartner((prev) => prev.filter((x) => x.id !== p.id));
    } catch (err) {
      alert(err?.message || 'Löschen fehlgeschlagen.');
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Strategische Partner</h1>
          <p className="mt-1 text-sm text-slate-600">
            Empfehler-Netzwerk: Steuerberater, Anwälte, Versicherungen u. ä.
          </p>
        </div>
        <button type="button" className="btn-primary" onClick={openCreate}>
          + Neuer Partner
        </button>
      </div>

      <div className="card space-y-4">
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="mr-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
              Typ:
            </span>
            {TYP_FILTER_OPTIONS.map((opt) => {
              const isActive = typFilter === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setTypFilter(opt.value)}
                  className={`inline-flex items-center rounded-full px-4 py-1.5 text-sm font-medium transition ${
                    isActive
                      ? 'bg-primary-600 text-white'
                      : 'bg-primary-50 text-primary-700 hover:bg-primary-100'
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>

          <input
            type="search"
            placeholder="Suche nach Name, Kontaktperson oder E-Mail …"
            className="input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {error && (
          <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 ring-1 ring-red-200">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Name
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Typ
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Kontaktperson
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Empfehlungen
                </th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {loading && (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-sm text-slate-500">
                    Lade …
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-sm text-slate-500">
                    Keine Partner gefunden.
                  </td>
                </tr>
              )}
              {!loading &&
                filtered.map((p) => {
                  const count = praxenCounts.get(p.id) ?? 0;
                  return (
                    <tr key={p.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <Link
                          to={`/partner/${p.id}`}
                          className="font-medium text-slate-900 hover:text-primary-700 hover:underline"
                        >
                          {p.name}
                        </Link>
                        {p.email && (
                          <div>
                            <a
                              href={`mailto:${p.email}`}
                              className="text-xs text-primary-700 hover:underline"
                            >
                              {p.email}
                            </a>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${
                            PARTNER_TYP_BADGE[p.typ] ?? 'bg-slate-100 text-slate-700 ring-slate-200'
                          }`}
                        >
                          {PARTNER_TYP_LABEL[p.typ] ?? p.typ}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {p.kontaktperson || '—'}
                        {p.telefon && (
                          <div className="text-xs text-slate-500">{p.telefon}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {count > 0 ? (
                          <Link
                            to={`/praxen?partner=${p.id}`}
                            className="font-medium text-primary-700 hover:underline"
                          >
                            {count} Praxis{count === 1 ? '' : 'en'}
                          </Link>
                        ) : (
                          <span className="text-slate-400">keine</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-3">
                          <button
                            type="button"
                            className="text-sm font-medium text-primary-600 hover:text-primary-800"
                            onClick={() => openEdit(p)}
                          >
                            Bearbeiten
                          </button>
                          <button
                            type="button"
                            className="text-sm font-medium text-red-600 hover:text-red-800"
                            onClick={() => handleDelete(p)}
                          >
                            Löschen
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>

      <PartnerFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        partner={editTarget}
        onSaved={() => load()}
      />
    </div>
  );
}
